// ============================================
// ACTIVIDAD-GLOBAL (2026-07-11) — timeline de TODO lo que pasa con expedientes
// ============================================
// Pestana "Actividad" de Expedientes. Vista AGREGADA y de SOLO LECTURA del
// mismo historial firmado que ya existe por expediente (transparencia total:
// visible para cualquier usuario con acceso al modulo). Fuente:
// GET /api/oportunidades/actividad (paginado con "Cargar mas", nunca todo de golpe).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FolderPlus, ArrowRight, Undo2, CornerUpRight, RotateCcw, Users, Pencil,
  PenLine, Printer, Bell, Trophy, XCircle, Archive, Search, Activity,
} from 'lucide-react';
import { API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloTarjeta } from '../helpersClientes';
import { etiquetaAutor } from './helpersExpedientes';
import { useResponsive } from '../../hooks/useResponsive';
import { authHeaders } from '../../utils/storage';

const PAGINA = 50;

// Catalogo visual de acciones. `frase(e)` arma el texto humano de la fila.
const ACCIONES = {
  creacion: {
    label: 'Creacion', Icono: FolderPlus, color: '#1D9E75',
    frase: () => 'creo el expediente',
  },
  avance: {
    label: 'Avance de paso', Icono: ArrowRight, color: '#185FA5',
    frase: (e) => `avanzo a "${e.estado_nuevo_nombre || e.estado_nuevo}"`,
  },
  regreso: {
    label: 'Regreso de paso', Icono: Undo2, color: '#b45309',
    frase: (e) => `regreso el expediente a "${e.estado_nuevo_nombre || e.estado_nuevo}"`,
  },
  salto: {
    label: 'Salto de paso', Icono: CornerUpRight, color: '#b45309',
    frase: (e) => `salto directo a "${e.estado_nuevo_nombre || e.estado_nuevo}"`,
  },
  reapertura: {
    label: 'Reapertura', Icono: RotateCcw, color: '#185FA5',
    frase: () => 'reabrio el expediente',
  },
  reasignacion: {
    label: 'Reasignacion', Icono: Users, color: '#7c3aed',
    frase: () => 'reasigno el responsable',
  },
  edicion: {
    label: 'Edicion de datos', Icono: Pencil, color: '#64748b',
    frase: () => 'edito los datos del expediente',
  },
  edicion_retroactiva: {
    label: 'Edicion retroactiva', Icono: PenLine, color: '#b45309',
    frase: () => 'edito un registro de un paso ya completado',
  },
  impresion_portada: {
    label: 'Impresion de portada', Icono: Printer, color: '#64748b',
    frase: () => 'imprimio la portada fisica',
  },
  recordatorio_responsable: {
    label: 'Aviso al responsable', Icono: Bell, color: '#185FA5',
    frase: () => 'aviso al responsable',
  },
  cierre_ganado: {
    label: 'Cierre ganado', Icono: Trophy, color: '#1D9E75',
    frase: () => 'quedo cerrado como GANADO',
  },
  cierre_perdido: {
    label: 'Cierre perdido', Icono: XCircle, color: '#dc2626',
    frase: () => 'cerro el expediente como PERDIDO',
  },
  cierre_descartado: {
    label: 'Descarte', Icono: Archive, color: '#6b7280',
    frase: () => 'descarto el expediente',
  },
};
const ORDEN_FILTRO_ACCION = [
  'creacion', 'avance', 'regreso', 'salto', 'reapertura', 'reasignacion',
  'edicion', 'edicion_retroactiva', 'impresion_portada', 'recordatorio_responsable',
  'cierre_ganado', 'cierre_perdido', 'cierre_descartado',
];

function fmtFH(f) {
  if (!f) return '';
  try {
    return new Date(f).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(f); }
}

const estiloSelect = {
  padding: '10px 12px', borderRadius: 8, border: `1px solid ${COLOR_BORDE}`,
  fontSize: 13, fontFamily: PP, color: COLOR_OSCURO, background: '#fff',
  minHeight: 44, boxSizing: 'border-box', maxWidth: '100%',
};

// `usuarios` llega del padre (Expedientes ya carga /api/usuarios para sus
// propios filtros — misma fuente, cero fetch duplicado).
export default function ActividadExpedientes({ onAbrirExpediente = null, usuarios = [] }) {
  const { isMobile } = useResponsive();
  const [filas, setFilas] = useState([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [error, setError] = useState(null);

  // Filtros
  const [usuarioId, setUsuarioId] = useState('');
  const [accion, setAccion] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const timerQ = useRef(null);

  // Debounce del buscador (350ms) para no pegarle al backend en cada tecla.
  useEffect(() => {
    clearTimeout(timerQ.current);
    timerQ.current = setTimeout(() => setQDeb(q.trim()), 350);
    return () => clearTimeout(timerQ.current);
  }, [q]);

  const armarQS = useCallback((offset) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(PAGINA));
    qs.set('offset', String(offset));
    if (usuarioId) qs.set('usuario_id', usuarioId);
    if (accion)    qs.set('accion', accion);
    if (desde)     qs.set('desde', desde);
    if (hasta)     qs.set('hasta', hasta);
    if (qDeb)      qs.set('q', qDeb);
    return qs.toString();
  }, [usuarioId, accion, desde, hasta, qDeb]);

  // Carga inicial / cambio de filtros: resetea a la primera pagina.
  useEffect(() => {
    let vivo = true;
    setCargando(true); setError(null);
    fetch(`${API_BASE_CLI}/api/oportunidades/actividad?${armarQS(0)}`, { headers: authHeaders() })
      .then(x => x.json())
      .then(r => {
        if (!vivo) return;
        if (!r || r.ok !== true) throw new Error((r && r.error) || 'Respuesta invalida');
        setFilas(r.actividad || []);
        setHayMas(!!r.hay_mas);
      })
      .catch(e => { if (vivo) { setError(e.message); setFilas([]); setHayMas(false); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [armarQS]);

  const cargarMas = async () => {
    if (cargandoMas) return;
    setCargandoMas(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/actividad?${armarQS(filas.length)}`,
        { headers: authHeaders() }).then(x => x.json());
      if (r && r.ok === true) {
        setFilas(prev => [...prev, ...(r.actividad || [])]);
        setHayMas(!!r.hay_mas);
      }
    } catch { /* la pagina actual queda intacta */ }
    finally { setCargandoMas(false); }
  };

  const hayFiltros = usuarioId || accion || desde || hasta || qDeb;
  const limpiar = () => { setUsuarioId(''); setAccion(''); setDesde(''); setHasta(''); setQ(''); };

  // Agrupar por dia (visual): [{ dia, items }]
  const grupos = useMemo(() => {
    const out = [];
    let actual = null;
    for (const f of filas) {
      const dia = f.fecha ? new Date(f.fecha).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
      if (!actual || actual.dia !== dia) { actual = { dia, items: [] }; out.push(actual); }
      actual.items.push(f);
    }
    return out;
  }, [filas]);

  return (
    <div style={{ minWidth: 0 }}>
      {/* FILTROS propios de la actividad */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 16,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ position: 'relative' }}>
          <Search size={15} strokeWidth={2} style={{ position: 'absolute', left: 12, top: 14, color: COLOR_GRIS_CLARO }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por folio, titulo o nota..."
            style={{
              width: '100%', padding: '10px 12px 10px 36px', minHeight: 44,
              border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
              fontSize: 16, fontFamily: PP, color: COLOR_OSCURO,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} style={estiloSelect} aria-label="Filtrar por persona">
            <option value="">Todas las personas</option>
            {(usuarios || []).filter(u => u && u.id && u.nombre).map(u => (
              <option key={u.id} value={u.id}>{u.nombre}</option>
            ))}
          </select>
          <select value={accion} onChange={(e) => setAccion(e.target.value)} style={estiloSelect} aria-label="Filtrar por accion">
            <option value="">Todas las acciones</option>
            {ORDEN_FILTRO_ACCION.map(a => <option key={a} value={a}>{ACCIONES[a].label}</option>)}
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLOR_GRIS }}>
            Desde
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} style={estiloSelect} aria-label="Desde" />
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLOR_GRIS }}>
            Hasta
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} style={estiloSelect} aria-label="Hasta" />
          </label>
          {hayFiltros && (
            <button
              onClick={limpiar}
              style={{
                padding: '10px 14px', minHeight: 44, borderRadius: 8, border: `1px solid ${COLOR_BORDE}`,
                background: '#fff', color: COLOR_GRIS, fontSize: 13, fontWeight: 600,
                fontFamily: PP, cursor: 'pointer',
              }}
            >Limpiar</button>
          )}
        </div>
      </div>

      {/* TIMELINE */}
      {cargando && (
        <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO, fontSize: 13 }}>
          Cargando actividad...
        </div>
      )}
      {!cargando && error && (
        <div style={{ ...estiloTarjeta, padding: 16, color: '#b91c1c', fontSize: 13 }}>
          No se pudo cargar la actividad: {error}
        </div>
      )}
      {!cargando && !error && filas.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '50px 20px', background: '#fff',
          borderRadius: 12, border: `1px dashed ${COLOR_BORDE}`, color: COLOR_GRIS,
        }}>
          <Activity size={36} strokeWidth={1.5} style={{ opacity: 0.35, marginBottom: 8 }} />
          <div style={{ fontWeight: 700, fontSize: 15, color: COLOR_OSCURO }}>
            {hayFiltros ? 'Nada coincide con esos filtros' : 'Aun no hay actividad registrada'}
          </div>
        </div>
      )}

      {!cargando && !error && grupos.map(g => (
        <div key={g.dia} style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: COLOR_GRIS, textTransform: 'capitalize',
            margin: '0 0 8px 2px',
          }}>{g.dia}</div>
          <div style={{ ...estiloTarjeta, padding: 0, overflow: 'hidden' }}>
            {g.items.map((e, idx) => {
              const acc = ACCIONES[e.accion] || {
                label: e.accion, Icono: Activity, color: COLOR_GRIS, frase: () => e.accion,
              };
              const Icono = acc.Icono;
              return (
                <div
                  key={e.uid}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: isMobile ? '12px 12px' : '12px 16px',
                    borderTop: idx === 0 ? 'none' : `1px solid ${COLOR_BORDE}`,
                  }}
                >
                  <span style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: `${acc.color}18`, color: acc.color,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 2,
                  }}>
                    <Icono size={15} strokeWidth={2} />
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13.5, color: COLOR_OSCURO, lineHeight: 1.45 }}>
                      <strong>{etiquetaAutor(e.usuario_nombre, e.nota)}</strong>{' '}
                      {acc.frase(e)}{' '}
                      <button
                        onClick={() => { if (typeof onAbrirExpediente === 'function') onAbrirExpediente(e.oportunidad_id); }}
                        title={e.titulo || e.folio}
                        style={{
                          border: 'none', background: 'none', padding: '4px 2px', cursor: 'pointer',
                          color: '#185FA5', fontWeight: 700, fontFamily: PP, fontSize: 13,
                          textDecoration: 'underline', textUnderlineOffset: 2,
                          // Area de toque real >=44px (hallazgo Don Porfirio: 28px medidos)
                          minHeight: 44, display: 'inline-flex', alignItems: 'center',
                          verticalAlign: 'middle',
                        }}
                      >{e.folio}</button>
                      {e.titulo && !isMobile && (
                        <span style={{ color: COLOR_GRIS_CLARO }}> · {e.titulo}</span>
                      )}
                    </div>
                    {e.nota && (
                      <div style={{
                        fontSize: 12.5, color: COLOR_GRIS, marginTop: 3, lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                      }}>{e.nota}</div>
                    )}
                    <div style={{ fontSize: 11.5, color: COLOR_GRIS_CLARO, marginTop: 3 }}>{fmtFH(e.fecha)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!cargando && !error && hayMas && (
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <button
            onClick={cargarMas}
            disabled={cargandoMas}
            style={{
              padding: '12px 22px', minHeight: 44, borderRadius: 10, border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', color: COLOR_OSCURO, fontSize: 13, fontWeight: 700,
              fontFamily: PP, cursor: cargandoMas ? 'default' : 'pointer', opacity: cargandoMas ? 0.6 : 1,
            }}
          >{cargandoMas ? 'Cargando...' : 'Cargar mas actividad'}</button>
        </div>
      )}
    </div>
  );
}
